import React, { createContext, useContext, useEffect, useState } from "react";

const CartContext = createContext(null);
const KEY = "alteza_cart_v1";

const lineKey = (pid, vid) => `${pid}::${vid || ""}`;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        // migrate older carts that have no line key / variant fields
        setItems(parsed.map((i) => ({
          ...i,
          key: i.key || lineKey(i.product_id, i.variant_id),
          variant_id: i.variant_id || null,
          variant_label: i.variant_label || "",
        })));
      }
    } catch {}
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return; // don't overwrite storage before the initial read completes
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items, hydrated]);

  const add = (product, qty = 1, variant = null) => {
    setItems((prev) => {
      const key = lineKey(product.id, variant?.id);
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => (i.key === key ? { ...i, quantity: i.quantity + qty } : i));
      }
      return [
        ...prev,
        {
          key,
          product_id: product.id,
          variant_id: variant?.id || null,
          variant_label: variant?.label || "",
          title: product.title,
          price: variant ? Number(variant.price) : Number(product.price),
          image: variant?.image || product.images?.[0] || "",
          quantity: qty,
        },
      ];
    });
  };

  const update = (key, quantity) =>
    setItems((prev) =>
      prev.map((i) => (i.key === key ? { ...i, quantity } : i)).filter((i) => i.quantity > 0)
    );

  const remove = (key) => setItems((prev) => prev.filter((i) => i.key !== key));

  const clear = () => setItems([]);

  const total = items.reduce((s, i) => s + Number(i.price) * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, add, update, remove, clear, total, count }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
